import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { LocationPicker, emptyLocation } from '../components/LocationPicker';
import { Building2, ArrowRight, Search, Plus } from 'lucide-react';
import { projectAPI, clientAPI, leadAPI, architectAssignAPI, settingsAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const money = (v) => (v == null ? '\u2014' : `\u20b9${Number(v).toLocaleString('en-IN')}`);
const field = 'w-full px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm';

export const ProjectFormPage = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const leadId = params.get('lead') || null;
  const presetClientId = params.get('client') || null;

  const idempotencyKey = useRef(`pf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const [lead, setLead] = useState(null);
  const [services, setServices] = useState([]);
  const [archIntel, setArchIntel] = useState(null);
  const [clientMode, setClientMode] = useState(leadId ? 'auto' : 'existing');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [newClient, setNewClient] = useState({ client_type: 'b2c', name: '', company_name: '', primary_mobile: '', city: '', address: '' });
  const [form, setForm] = useState({ project_name: '', service: '', requirement_notes: '', project_address: '', city: '', final_deal_amount: '', assigned_architect_id: '', ...emptyLocation });
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);

  const canEditContract = hasPermission('payment.contract_value.edit');
  const canCreateClient = hasPermission('clients.create');
  const canViewClients = hasPermission('clients.view');

  useEffect(() => { settingsAPI.getServices().then((r) => setServices(r.data || [])).catch(() => {}); }, []);
  // B5E: launched from a Client Overview - reuse that client, never duplicate it
  useEffect(() => {
    if (!presetClientId) return;
    clientAPI.get(presetClientId)
      .then((r) => { setSelectedClient(r.data); setClientMode('linked'); })
      .catch(() => {});
  }, [presetClientId]);
  useEffect(() => { architectAssignAPI.intelligence().then((r) => setArchIntel(r.data)).catch(() => { /* not authorized to assign */ }); }, []);

  // Launched from a Lead: prelink the source lead + its client, reuse its information
  useEffect(() => {
    if (!leadId) return;
    leadAPI.getById(leadId).then(async (r) => {
      const l = r.data;
      setLead(l);
      setForm((f) => ({
        ...f,
        project_name: f.project_name || l.project_name || l.customer_name || '',
        service: f.service || (l.services_required || [])[0] || l.service || '',
        project_address: f.project_address || l.project_address || l.address || '',
        city: f.city || l.city || '',
        // inherit the lead's standardized location
        country_code: f.country_code || l.country_code || '', country: f.country || l.country || '',
        state_code: f.state_code || l.state_code || '', state: f.state || l.state || '',
        district_code: f.district_code || l.district_code || '', district: f.district || l.district || '',
        locality: f.locality || l.locality || '',
      }));
      if (l.client_id && canViewClients) {
        try { const c = await clientAPI.get(l.client_id); setSelectedClient(c.data); setClientMode('linked'); return; } catch (e) { /* fall through */ }
      }
      if (l.client_id) { setSelectedClient({ client_id: l.client_id, name: l.customer_name }); setClientMode('linked'); return; }
      setClientMode('auto');
      setNewClient((n) => ({
        ...n,
        client_type: l.lead_type === 'b2b' || l.partner_id ? 'b2b' : 'b2c',
        name: l.customer_name || '', company_name: l.company_name || '',
        primary_mobile: l.mobile || l.phone || '', city: l.city || '', address: l.address || '',
      }));
    }).catch(() => toast.error('Could not load the source lead'));
  }, [leadId, canViewClients]);

  const doSearch = useCallback(async () => {
    if (!canViewClients) return;
    try { const r = await clientAPI.list({ search: clientSearch || undefined, limit: 20 }); setClientResults(r.data || []); }
    catch (e) { toast.error('Not permitted to view clients'); }
  }, [clientSearch, canViewClients]);

  useEffect(() => { if (!leadId && canViewClients) doSearch(); /* initial list */ }, [leadId, canViewClients, doSearch]);

  const architects = useMemo(() => archIntel?.architects || [], [archIntel]);

  const submit = async () => {
    if (!form.project_name.trim()) { toast.error('Project Name is required'); return; }
    const payload = {
      source_lead_id: leadId || null,
      project_name: form.project_name.trim(),
      service: form.service || null,
      services_required: form.service ? [form.service] : [],
      requirement_notes: form.requirement_notes || null,
      project_address: form.project_address || null,
      city: form.city || null,
      country_code: form.country_code || null, country: form.country || null,
      state_code: form.state_code || null, state: form.state || null,
      district_code: form.district_code || null, district: form.district || null,
      locality: form.locality || null,
      final_deal_amount: canEditContract && form.final_deal_amount !== '' ? Number(form.final_deal_amount) : null,
      assigned_architect_id: form.assigned_architect_id || null,
      idempotency_key: idempotencyKey.current,
    };
    if (clientMode === 'linked' || clientMode === 'existing') {
      const cid = selectedClient?.client_id || selectedClient?.id;
      if (!cid) { toast.error('Select a Client'); return; }
      payload.client_id = cid;
    } else {
      if (!newClient.name.trim() || !newClient.primary_mobile.trim()) { toast.error('Client name and mobile are required'); return; }
      payload.client = { ...newClient };
    }
    setSaving(true);
    try {
      const r = await projectAPI.create(payload);
      setCreated(r.data);
      toast.success(`Project created · ${r.data.project_serial}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create project');
    } finally { setSaving(false); }
  };

  if (created) {
    return (
      <div className="max-w-2xl space-y-5" data-testid="project-created">
        <div className="clay-card p-6 space-y-3">
          <h1 className="text-2xl font-outfit font-semibold">Project Created</h1>
          <p className="font-mono text-lg font-semibold" data-testid="created-project-serial">{created.project_serial}</p>
          <p className="text-sm text-muted-foreground">{created.project_name}{created.service ? ` · ${created.service}` : ''}</p>
          {created.final_deal_amount ? <p className="text-sm">Contract Value: <span className="font-medium">{money(created.final_deal_amount)}</span></p> : null}
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={() => navigate(`/projects/${created.project_id}`)} data-testid="open-project-btn"
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg text-sm">
              Open Project <ArrowRight className="h-4 w-4" />
            </button>
            {leadId && <Link to={`/leads/${leadId}?tab=project`} data-testid="back-to-lead-btn"
              className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Back to Lead</Link>}
            <button onClick={() => { idempotencyKey.current = `pf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`; setCreated(null); setForm({ project_name: '', service: '', requirement_notes: '', project_address: '', city: '', final_deal_amount: '', assigned_architect_id: '' }); }}
              data-testid="create-another-project-btn" className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">
              Create Another Project
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5" data-testid="project-form-page">
      <div>
        <h1 className="text-3xl font-outfit font-semibold flex items-center gap-2"><Building2 className="h-6 w-6 text-amber-600" /> Create Project</h1>
        <p className="text-muted-foreground mt-1" data-testid="project-form-context">
          {leadId ? `From Lead${lead ? ` · ${lead.lead_no || lead.customer_name || ''}` : ''}` : 'Direct Project — no source lead'}
        </p>
      </div>

      {/* Client */}
      <div className="clay-card p-5 space-y-3" data-testid="project-client-section">
        <h2 className="text-base font-medium">Client</h2>
        {clientMode === 'linked' && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-3 text-sm dark:border-slate-700" data-testid="prelinked-client">
            <div>
              <p className="font-medium">{selectedClient?.name}{selectedClient?.company_name ? ` · ${selectedClient.company_name}` : ''}</p>
              <p className="text-xs text-muted-foreground">
                {(selectedClient?.client_type || '').toUpperCase() || 'CLIENT'}{selectedClient?.primary_mobile ? ` · ${selectedClient.primary_mobile}` : ''}{leadId ? ' · already linked to this Lead' : presetClientId ? ' · adding another project for this client' : ''}
              </p>
            </div>
          </div>
        )}
        {clientMode === 'auto' && (
          <>
            <p className="text-xs text-muted-foreground">This Lead has no Client record yet — one will be created from the Lead's own details (no duplicate record is made for later projects).</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <select value={newClient.client_type} onChange={(e) => setNewClient({ ...newClient, client_type: e.target.value })} className={field} data-testid="new-client-type">
                <option value="b2c">B2C</option><option value="b2b">B2B</option>
              </select>
              <input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Client name" className={field} data-testid="new-client-name" />
              <input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} placeholder="Company (B2B)" className={field} data-testid="new-client-company" />
              <input value={newClient.primary_mobile} onChange={(e) => setNewClient({ ...newClient, primary_mobile: e.target.value })} placeholder="Mobile" className={field} data-testid="new-client-mobile" />
            </div>
            {!canCreateClient && <p className="text-xs text-red-600" data-testid="client-create-denied">Creating a Client requires the Create Client permission.</p>}
          </>
        )}
        {!leadId && (
          <>
            <div className="flex gap-2">
              <button onClick={() => setClientMode('existing')} data-testid="client-mode-existing"
                className={`px-3 py-1.5 rounded-lg text-sm border ${clientMode === 'existing' ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-slate-700'}`}>Existing Client</button>
              <button onClick={() => setClientMode('new')} disabled={!canCreateClient} data-testid="client-mode-new"
                className={`px-3 py-1.5 rounded-lg text-sm border disabled:opacity-40 ${clientMode === 'new' ? 'bg-primary text-white border-primary' : 'border-slate-200 dark:border-slate-700'}`}>
                <Plus className="mr-1 inline h-3.5 w-3.5" />New Client
              </button>
            </div>
            {clientMode === 'existing' && (canViewClients ? (
              <>
                <div className="flex gap-2">
                  <input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Search by name, company or mobile" className={field} data-testid="client-search-input" />
                  <button onClick={doSearch} data-testid="client-search-btn" className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm"><Search className="h-4 w-4" /></button>
                </div>
                <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800" data-testid="client-results">
                  {clientResults.length === 0 ? <p className="py-3 text-sm text-muted-foreground">No clients found.</p> : clientResults.map((c) => (
                    <button key={c.client_id} onClick={() => setSelectedClient(c)} data-testid={`client-option-${c.client_id}`}
                      className={`w-full px-2 py-2 text-left text-sm ${(selectedClient?.client_id === c.client_id) ? 'bg-amber-50 dark:bg-amber-900/20' : ''}`}>
                      <span className="font-medium">{c.name}</span>{c.company_name ? ` · ${c.company_name}` : ''}
                      <span className="ml-2 text-xs text-muted-foreground">{(c.client_type || '').toUpperCase()}{c.primary_mobile ? ` · ${c.primary_mobile}` : ''}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : <p className="text-xs text-red-600" data-testid="client-view-denied">Selecting an existing Client requires the View Clients permission.</p>)}
            {clientMode === 'new' && (
              <div className="grid gap-3 sm:grid-cols-2">
                <select value={newClient.client_type} onChange={(e) => setNewClient({ ...newClient, client_type: e.target.value })} className={field} data-testid="new-client-type">
                  <option value="b2c">B2C</option><option value="b2b">B2B</option>
                </select>
                <input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Client name" className={field} data-testid="new-client-name" />
                <input value={newClient.company_name} onChange={(e) => setNewClient({ ...newClient, company_name: e.target.value })} placeholder="Company (B2B)" className={field} data-testid="new-client-company" />
                <input value={newClient.primary_mobile} onChange={(e) => setNewClient({ ...newClient, primary_mobile: e.target.value })} placeholder="Mobile" className={field} data-testid="new-client-mobile" />
                <input value={newClient.city} onChange={(e) => setNewClient({ ...newClient, city: e.target.value })} placeholder="City" className={field} data-testid="new-client-city" />
                <input value={newClient.address} onChange={(e) => setNewClient({ ...newClient, address: e.target.value })} placeholder="Address" className={field} data-testid="new-client-address" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Project */}
      <div className="clay-card p-5 space-y-3" data-testid="project-details-section">
        <h2 className="text-base font-medium">Project</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} placeholder="Project Name" className={field} data-testid="project-name-input" />
          <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={field} data-testid="project-service-select">
            <option value="">Select Service</option>
            {services.map((s) => <option key={s.id || s.name} value={s.name}>{s.name}</option>)}
          </select>
          <input value={form.project_address} onChange={(e) => setForm({ ...form, project_address: e.target.value })} placeholder="Street / Site Address" className={field} data-testid="project-address-input" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <LocationPicker idPrefix="project-loc" value={form}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} />
        </div>
        <textarea value={form.requirement_notes} onChange={(e) => setForm({ ...form, requirement_notes: e.target.value })} rows={3} placeholder="Requirement" className={field} data-testid="project-requirement-input" />
        {canEditContract ? (
          <div>
            <label className="mb-1 block text-xs font-medium">Contract / Final Deal Amount</label>
            <input type="number" value={form.final_deal_amount} onChange={(e) => setForm({ ...form, final_deal_amount: e.target.value })} placeholder="0" className={field} data-testid="project-contract-input" />
            <p className="mt-1 text-xs text-muted-foreground">This project keeps its own contract ledger. Payments are recorded on the project itself.</p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid="contract-value-denied">Setting a Contract Value requires the Edit Contract Value permission — the project can be created without it.</p>
        )}
      </div>

      {/* Architect — only where the caller may assign */}
      {architects.length > 0 && (
        <div className="clay-card p-5 space-y-3" data-testid="project-architect-section">
          <h2 className="text-base font-medium">Assign Architect (optional)</h2>
          {archIntel?.recommended_user_id && (
            <p className="text-xs text-primary" data-testid="architect-recommendation">
              Recommended: {(architects.find((a) => a.user_id === archIntel.recommended_user_id) || {}).name}{archIntel.recommended_reason ? ` — ${archIntel.recommended_reason}` : ''}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground"><tr><th className="py-1">Architect</th><th>Running</th><th>Not Started</th><th>Waiting</th><th>Revisions</th><th>Urgent</th><th>Workload</th><th /></tr></thead>
              <tbody>
                {architects.map((a) => (
                  <tr key={a.user_id} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="py-1.5">{a.name}</td><td>{a.running_projects}</td><td>{a.not_started}</td><td>{a.waiting_approval}</td>
                    <td>{a.revisions}</td><td>{a.urgent_projects}</td><td data-testid={`pf-arch-workload-${a.user_id}`}>{a.current_workload}</td>
                    <td>
                      <button onClick={() => setForm({ ...form, assigned_architect_id: form.assigned_architect_id === a.user_id ? '' : a.user_id })}
                        data-testid={`pf-arch-select-${a.user_id}`}
                        className={`rounded px-2 py-1 text-xs ${form.assigned_architect_id === a.user_id ? 'bg-primary text-white' : 'border border-slate-200 dark:border-slate-700'}`}>
                        {form.assigned_architect_id === a.user_id ? 'Selected' : 'Select'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} data-testid="project-submit-btn"
          className="px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-60">
          {saving ? 'Creating\u2026' : 'Create Project'}
        </button>
        <button onClick={() => navigate(leadId ? `/leads/${leadId}?tab=project` : '/projects')} data-testid="project-cancel-btn"
          className="px-5 py-2.5 border border-slate-200 dark:border-slate-700 rounded-lg text-sm">Cancel</button>
      </div>
    </div>
  );
};

export default ProjectFormPage;
